/**
 * Affinity Programmes admin panel — creates and manages customer-facing
 * membership programmes (e.g. ARTISTEN) that surface member benefits
 * on ``/register/{slug}`` and drive per-product-family discounts
 * through the canonical pricing resolver.
 *
 * Distinct from the SUPPLIER partners panel (``PartnersPage``); this
 * one is DB-driven and reused via /api/admin/partner-programmes.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash2, Users, Upload, Image as ImageIcon, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { API_BASE } from '../../lib/apiBase';

const API = `${API_BASE}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const FAMILIES = ['vinyl', 'cd', 'dvd', 'bluray'];

// Currencies the finance side already knows about — kept as a small
// closed list so the admin picker matches production-grade Owntune
// customers. Blank ("") means "no programme default; use existing
// company/country/global fallback".
const CURRENCY_CHOICES = [
    { value: '', label: 'No default (use existing fallback)' },
    { value: 'EUR', label: 'EUR — Euro' },
    { value: 'DKK', label: 'DKK — Danish krone' },
    { value: 'SEK', label: 'SEK — Swedish krona' },
    { value: 'NOK', label: 'NOK — Norwegian krone' },
    { value: 'GBP', label: 'GBP — British pound' },
    { value: 'USD', label: 'USD — US dollar' },
];

const EMPTY_FORM = {
    slug: '', name: '', active: true,
    headline: '', description: '',
    discount_by_family: { vinyl: 0, cd: 0, dvd: 0, bluray: 0 },
    default_currency: '',
};

export default function PartnerProgrammesPanel() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);   // null = create; row = edit
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/partner-programmes?include_inactive=true`, {
                headers: authHeader(),
            });
            setRows(res.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load affinity programmes');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setDialogOpen(true);
    };
    const openEdit = (row) => {
        setEditing(row);
        setForm({
            slug: row.slug,
            name: row.name,
            active: row.active !== false,
            headline: row.headline || '',
            description: row.description || '',
            discount_by_family: FAMILIES.reduce((acc, f) => {
                acc[f] = Number(row.discount_by_family?.[f] || 0);
                return acc;
            }, {}),
            default_currency: row.default_currency || '',
        });
        setDialogOpen(true);
    };

    // Image upload — bytes are stored in the login_page_images
    // collection but scoped to this programme (never appear on the
    // global /login or /register pages).
    const [uploading, setUploading] = useState(false);
    const handleImageUpload = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';   // allow re-selecting the same file
        if (!f || !editing) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', f);
            await axios.post(
                `${API}/admin/partner-programmes/${editing.id}/image`,
                fd,
                { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } },
            );
            toast.success('Image uploaded');
            const refreshed = await axios.get(
                `${API}/admin/partner-programmes/${editing.id}`,
                { headers: authHeader() },
            );
            setEditing(refreshed.data);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };
    const handleImageRemove = async () => {
        if (!editing) return;
        if (!window.confirm('Remove the registration page image for this partner?')) return;
        try {
            await axios.delete(
                `${API}/admin/partner-programmes/${editing.id}/image`,
                { headers: authHeader() },
            );
            toast.success('Image removed');
            const refreshed = await axios.get(
                `${API}/admin/partner-programmes/${editing.id}`,
                { headers: authHeader() },
            );
            setEditing(refreshed.data);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Remove failed');
        }
    };
    const editingImageUrl = editing?.right_side_image_ids?.[0]
        ? `${API_BASE}/api/auth/login-page-images/${editing.right_side_image_ids[0]}/raw?t=${editing.updated_at || ''}`
        : null;

    // ---- Detail / Members dialog ----------------------------------------
    // A single "detail" dialog opened by clicking a row/name. Shows the
    // programme's discount grid, headline/description and the list of
    // linked customer organizations with a per-row Remove action.
    const [detailProgramme, setDetailProgramme] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [members, setMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [removingMember, setRemovingMember] = useState(null);

    const openDetail = async (row) => {
        setDetailProgramme(row);
        setDetailOpen(true);
        setMembers([]);
        setMembersLoading(true);
        try {
            const res = await axios.get(
                `${API}/admin/partner-programmes/${row.id}/members`,
                { headers: authHeader() },
            );
            setMembers(res.data || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to load members');
        } finally {
            setMembersLoading(false);
        }
    };

    const handleRemoveMember = async (m) => {
        if (!detailProgramme) return;
        const label = m.organization_name || m.organization_id;
        if (!window.confirm(
            `Remove "${label}" from ${detailProgramme.name}?\n\n` +
            `The company, user, orders and billing data are NOT deleted — ` +
            `only the partner association is removed. Future pricing will ` +
            `fall back to Global Discount / base pricing.`
        )) return;
        setRemovingMember(m.organization_id);
        try {
            await axios.delete(
                `${API}/admin/partner-programmes/${detailProgramme.id}/members/${m.organization_id}`,
                { headers: authHeader() },
            );
            toast.success(`Removed ${label}`);
            setMembers((prev) => prev.filter((x) => x.organization_id !== m.organization_id));
            load(); // refresh member_count on the outer table
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Remove failed');
        } finally {
            setRemovingMember(null);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                slug: form.slug.trim().toLowerCase(),
                name: form.name.trim(),
                active: form.active,
                headline: form.headline.trim() || null,
                description: form.description.trim() || null,
                discount_by_family: Object.fromEntries(
                    Object.entries(form.discount_by_family || {})
                        .map(([k, v]) => [k, Number(v) || 0])
                ),
                // Empty string clears the default currency (falls back
                // to the existing country/EUR chain). Keep uppercase
                // so validation on the backend accepts it.
                default_currency: form.default_currency
                    ? form.default_currency.toUpperCase()
                    : null,
            };
            if (editing) {
                // PATCH — slug is not editable to keep member linkage stable.
                const { slug: _s, ...patch } = payload;
                await axios.patch(`${API}/admin/partner-programmes/${editing.id}`, patch, {
                    headers: authHeader(),
                });
                toast.success('Affinity programme updated');
            } else {
                await axios.post(`${API}/admin/partner-programmes`, payload, {
                    headers: authHeader(),
                });
                toast.success('Affinity programme created');
            }
            setDialogOpen(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDeactivate = async (row) => {
        if (!window.confirm(`Deactivate "${row.name}"? Existing members keep their association but new signups through /register/${row.slug} will fall back to a plain signup.`)) return;
        try {
            await axios.delete(`${API}/admin/partner-programmes/${row.id}`, { headers: authHeader() });
            toast.success('Programme deactivated');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Deactivate failed');
        }
    };

    return (
        <Card data-testid="partner-programmes-panel">
            <CardHeader className="flex flex-row items-start justify-between">
                <div>
                    <CardTitle className="text-base">Affinity Programmes</CardTitle>
                    <CardDescription>
                        Create membership programmes with a per-family discount grid.
                        Each programme has a public <code>/register/&#123;slug&#125;</code> landing
                        page that permanently associates new signups with the programme.
                    </CardDescription>
                </div>
                <Button onClick={openCreate} data-testid="partner-programme-create-btn" size="sm">
                    <Plus className="h-4 w-4 mr-1" /> New programme
                </Button>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                ) : rows.length === 0 ? (
                    <p className="text-sm text-slate-400">No affinity programmes yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="partner-programmes-table">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Name</th>
                                    <th className="text-left px-3 py-2 font-medium">Slug / URL</th>
                                    <th className="text-left px-3 py-2 font-medium">Discounts</th>
                                    <th className="text-right px-3 py-2 font-medium">Members</th>
                                    <th className="text-left px-3 py-2 font-medium">Status</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-t hover:bg-slate-50"
                                        data-testid={`partner-programme-row-${r.slug}`}>
                                        <td className="px-3 py-2 font-medium">
                                            <button
                                                type="button"
                                                onClick={() => openDetail(r)}
                                                className="text-left text-blue-600 hover:text-blue-800 hover:underline"
                                                data-testid={`partner-programme-open-detail-${r.slug}`}
                                            >
                                                {r.name}
                                            </button>
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs">
                                            <div>{r.slug}</div>
                                            <a href={`/register/${r.slug}`} target="_blank" rel="noreferrer"
                                                className="text-blue-600 hover:underline">
                                                /register/{r.slug}
                                            </a>
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            {FAMILIES.map((f) => {
                                                const pct = Number(r.discount_by_family?.[f] || 0);
                                                return pct > 0 ? (
                                                    <span key={f} className="inline-block mr-2">
                                                        <span className="capitalize">{f}</span>&nbsp;
                                                        <span className="font-semibold">{pct}%</span>
                                                    </span>
                                                ) : null;
                                            })}
                                            {FAMILIES.every((f) => !Number(r.discount_by_family?.[f])) && (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right text-xs">
                                            <button
                                                type="button"
                                                onClick={() => openDetail(r)}
                                                className="inline-flex items-center gap-1 text-slate-600 hover:text-blue-700 hover:underline"
                                                title="View member organizations"
                                                data-testid={`partner-programme-members-count-${r.slug}`}
                                            >
                                                <Users className="h-3 w-3" /> {r.member_count ?? 0}
                                            </button>
                                        </td>
                                        <td className="px-3 py-2">
                                            {r.active ? (
                                                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>
                                            ) : (
                                                <Badge className="bg-slate-100 text-slate-500 border-slate-200">Inactive</Badge>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <Button variant="ghost" size="sm" onClick={() => openEdit(r)}
                                                data-testid={`partner-programme-edit-${r.slug}`}>
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                            {r.active && (
                                                <Button variant="ghost" size="sm" onClick={() => handleDeactivate(r)}
                                                    data-testid={`partner-programme-deactivate-${r.slug}`}>
                                                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editing ? `Edit ${editing.name}` : 'New Affinity Programme'}</DialogTitle>
                        <DialogDescription>
                            Discounts are per Product Family. Zero means "no partner discount for that family" — the customer's Global Discount (if any) will apply as fallback.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>URL slug</Label>
                            <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                disabled={!!editing}
                                placeholder="artisten"
                                data-testid="partner-programme-slug-input" />
                            {!editing && (
                                <p className="text-xs text-slate-400 mt-0.5">
                                    Lowercase, letters, digits and dashes only. Public URL: <code>/register/&#123;slug&#125;</code>
                                </p>
                            )}
                        </div>
                        <div>
                            <Label>Partner name</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="ARTISTEN"
                                data-testid="partner-programme-name-input" />
                        </div>
                        <div>
                            <Label>Headline</Label>
                            <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })}
                                placeholder="Exclusive member pricing for vinyl and CD production" />
                        </div>
                        <div>
                            <Label>Description / benefits</Label>
                            <Textarea rows={3} value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                placeholder="Free release consultation before first production. Digital Owntune access to configure, order, track and reorder." />
                        </div>
                        {editing && (
                            <div className="border-t pt-3">
                                <Label>Registration page image</Label>
                                <p className="text-xs text-slate-500 mt-0.5 mb-1">
                                    Shown on <code>/register/{editing.slug}</code>. This image
                                    replaces the global Login Page image for this partner and is
                                    NOT displayed on the plain <code>/register</code> page.
                                </p>
                                <p className="text-xs text-slate-500 mb-2">
                                    Aspect ratio: <strong>4:5 (portrait)</strong>. Recommended
                                    dimensions: <strong>1600 × 2000 px</strong> (min 1200 × 1500 px).
                                    Only shown on desktop (≥1024&nbsp;px); image is cropped to fill.
                                </p>
                                {editingImageUrl ? (
                                    <div className="space-y-2" data-testid="partner-programme-image-preview">
                                        <div className="relative w-full max-w-[240px] aspect-[4/5] rounded-lg overflow-hidden bg-slate-100 border">
                                            <img src={editingImageUrl} alt=""
                                                className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex gap-2">
                                            <label className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border text-sm cursor-pointer hover:bg-slate-50">
                                                <Upload className="h-3.5 w-3.5" />
                                                <span>{uploading ? 'Uploading…' : 'Replace image'}</span>
                                                <input type="file" className="hidden"
                                                    accept="image/jpeg,image/png,image/webp"
                                                    onChange={handleImageUpload} disabled={uploading}
                                                    data-testid="partner-programme-image-replace-input" />
                                            </label>
                                            <Button type="button" variant="ghost" size="sm"
                                                onClick={handleImageRemove}
                                                data-testid="partner-programme-image-remove-btn">
                                                <Trash2 className="h-3.5 w-3.5 text-red-500 mr-1" /> Remove
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <label className="flex flex-col items-center justify-center w-full max-w-[240px] aspect-[4/5] rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 cursor-pointer text-sm text-slate-500">
                                        <ImageIcon className="h-6 w-6 mb-1 text-slate-400" />
                                        <span>{uploading ? 'Uploading…' : 'Click to upload'}</span>
                                        <span className="text-xs text-slate-400 mt-0.5 text-center px-2">JPG, PNG or WEBP · up to 8 MB<br/>Portrait 4:5 · 1600 × 2000 px</span>
                                        <input type="file" className="hidden"
                                            accept="image/jpeg,image/png,image/webp"
                                            onChange={handleImageUpload} disabled={uploading}
                                            data-testid="partner-programme-image-upload-input" />
                                    </label>
                                )}
                            </div>
                        )}
                        {!editing && (
                            <p className="text-xs text-slate-400 italic">
                                Save the programme first — you'll be able to upload its
                                registration page image after creation.
                            </p>
                        )}
                        <div>
                            <Label>Discount by Product Family (%)</Label>
                            <div className="grid grid-cols-4 gap-2 mt-1">
                                {FAMILIES.map((f) => (
                                    <div key={f}>
                                        <div className="text-xs text-slate-500 capitalize mb-0.5">{f}</div>
                                        <Input type="number" min="0" max="100" step="0.5"
                                            value={form.discount_by_family[f]}
                                            onChange={(e) => setForm({
                                                ...form,
                                                discount_by_family: {
                                                    ...form.discount_by_family,
                                                    [f]: e.target.value === '' ? 0 : Number(e.target.value),
                                                },
                                            })}
                                            data-testid={`partner-programme-discount-${f}-input`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="partner-programme-default-currency">
                                Default currency
                            </Label>
                            <p className="text-xs text-slate-500 mb-1">
                                First-render currency for anyone landing through{' '}
                                <code>/register/{form.slug || '{slug}'}</code>. Only
                                a default — the customer's own company / selected
                                currency always wins once available.
                            </p>
                            <select
                                id="partner-programme-default-currency"
                                className="w-full border border-slate-300 rounded-md h-10 px-3 text-sm bg-white"
                                value={form.default_currency || ''}
                                onChange={(e) => setForm({ ...form, default_currency: e.target.value })}
                                data-testid="partner-programme-default-currency-select"
                            >
                                {CURRENCY_CHOICES.map((c) => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })}
                                data-testid="partner-programme-active-switch" />
                            <Label className="mb-0">Active</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving || !form.slug || !form.name}
                            data-testid="partner-programme-save-btn">
                            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                            {editing ? 'Save changes' : 'Create programme'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="max-w-2xl" data-testid="partner-programme-detail-dialog">
                    <DialogHeader>
                        <DialogTitle>{detailProgramme?.name || 'Affinity Programme'}</DialogTitle>
                        <DialogDescription>
                            {detailProgramme?.headline || (
                                <span className="text-slate-400 italic">No headline set.</span>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    {detailProgramme && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-xs text-slate-500 mb-1">Public URL</p>
                                    <a href={`/register/${detailProgramme.slug}`} target="_blank" rel="noreferrer"
                                        className="text-blue-600 hover:underline font-mono">
                                        /register/{detailProgramme.slug}
                                    </a>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 mb-1">Status</p>
                                    {detailProgramme.active ? (
                                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>
                                    ) : (
                                        <Badge className="bg-slate-100 text-slate-500 border-slate-200">Inactive</Badge>
                                    )}
                                </div>
                                {detailProgramme.default_currency && (
                                    <div>
                                        <p className="text-xs text-slate-500 mb-1">Default currency</p>
                                        <span className="text-sm font-medium"
                                            data-testid="partner-programme-detail-default-currency">
                                            {detailProgramme.default_currency}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div>
                                <p className="text-xs text-slate-500 mb-1">Member discounts</p>
                                <div className="flex flex-wrap gap-2 text-sm">
                                    {FAMILIES.map((f) => {
                                        const pct = Number(detailProgramme.discount_by_family?.[f] || 0);
                                        return (
                                            <span key={f}
                                                className={`px-2 py-1 rounded-md border ${pct > 0
                                                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                                    : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                                                <span className="capitalize">{f}</span>{' '}
                                                <span className="font-semibold">{pct}%</span>
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                            {detailProgramme.description && (
                                <div>
                                    <p className="text-xs text-slate-500 mb-1">Description</p>
                                    <p className="text-sm text-slate-700 whitespace-pre-line">
                                        {detailProgramme.description}
                                    </p>
                                </div>
                            )}
                            <div className="border-t pt-3">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm font-medium text-slate-700">
                                        Member organizations
                                    </p>
                                    <span className="text-xs text-slate-500">
                                        {members.length} linked
                                    </span>
                                </div>
                                {membersLoading ? (
                                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                                        <Loader2 className="h-4 w-4 animate-spin" /> Loading members…
                                    </div>
                                ) : members.length === 0 ? (
                                    <p className="text-sm text-slate-400 italic">
                                        No customer organizations linked yet. New signups via{' '}
                                        <code>/register/{detailProgramme.slug}</code> and completions
                                        of the billing profile will appear here.
                                    </p>
                                ) : (
                                    <div className="overflow-x-auto border rounded-md">
                                        <table className="w-full text-sm" data-testid="partner-programme-members-table">
                                            <thead className="bg-slate-50">
                                                <tr>
                                                    <th className="text-left px-3 py-2 font-medium">Company</th>
                                                    <th className="text-left px-3 py-2 font-medium">Primary contact</th>
                                                    <th className="text-left px-3 py-2 font-medium">Joined</th>
                                                    <th className="text-left px-3 py-2 font-medium">Status</th>
                                                    <th className="px-3 py-2"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {members.map((m) => (
                                                    <tr key={m.organization_id} className="border-t"
                                                        data-testid={`partner-programme-member-row-${m.organization_id}`}>
                                                        <td className="px-3 py-2 font-medium">
                                                            {m.organization_name || (
                                                                <span className="text-slate-400 italic">Unnamed</span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {m.primary_contact_email ? (
                                                                <div>
                                                                    {m.primary_contact_name && (
                                                                        <div className="text-slate-700">{m.primary_contact_name}</div>
                                                                    )}
                                                                    <a href={`mailto:${m.primary_contact_email}`}
                                                                        className="text-xs text-blue-600 hover:underline">
                                                                        {m.primary_contact_email}
                                                                    </a>
                                                                </div>
                                                            ) : (
                                                                <span className="text-slate-400 italic text-xs">no contact</span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2 text-xs text-slate-600">
                                                            {m.joined_at
                                                                ? new Date(m.joined_at).toLocaleDateString()
                                                                : <span className="text-slate-400">—</span>}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {m.active ? (
                                                                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Active</Badge>
                                                            ) : (
                                                                <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-xs">Disabled</Badge>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2 text-right">
                                                            <Button variant="ghost" size="sm"
                                                                disabled={removingMember === m.organization_id}
                                                                onClick={() => handleRemoveMember(m)}
                                                                data-testid={`partner-programme-member-remove-${m.organization_id}`}
                                                                title="Remove from programme (does not delete company or orders)">
                                                                {removingMember === m.organization_id ? (
                                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                                ) : (
                                                                    <>
                                                                        <X className="h-3.5 w-3.5 text-red-500 mr-1" />
                                                                        <span className="text-xs">Remove</span>
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDetailOpen(false)}
                            data-testid="partner-programme-detail-close-btn">Close</Button>
                        {detailProgramme && (
                            <Button onClick={() => { setDetailOpen(false); openEdit(detailProgramme); }}
                                data-testid="partner-programme-detail-edit-btn">
                                <Pencil className="h-3.5 w-3.5 mr-1" /> Edit programme
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
